import { TriangleAlert } from 'lucide-react'
import dayjs from 'dayjs'
import RelativeTime from 'dayjs/plugin/relativeTime'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import LinkButton from '@/components/ui/link-button'
import { cn } from '@/lib/utils'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import Difficulty from 'app/frontend/types/custom/Difficulty'
import RaidPlan from 'app/frontend/types/custom/RaidPlan'
import TeamPlanCompositionSummary from './TeamPlanCompositionSummary'

dayjs.extend(RelativeTime)

type RaidPlanCandidatePickerProps = {
  plans: RaidPlan[]
  selectedPlanId?: number | null
  warningDifficulty?: Difficulty
  showOwnerBadge?: boolean
  teamPath: string
  onSelect: (plan: RaidPlan) => void
  emptyLabel?: string
  emptyActionLabel?: string
  emptyActionPath?: string
}

const RaidPlanCandidatePicker = ({
  plans,
  selectedPlanId,
  warningDifficulty,
  showOwnerBadge = false,
  teamPath,
  onSelect,
  emptyLabel = 'No raid plans found for this encounter',
  emptyActionLabel = 'View plans',
  emptyActionPath,
}: RaidPlanCandidatePickerProps) => {
  const { t } = useTranslation()

  if (!plans.length) {
    return (
      <>
        <p className='text-sm text-muted-foreground'>{emptyLabel}</p>
        {emptyActionPath ? (
          <LinkButton to={emptyActionPath} size='sm' className='w-full'>
            {emptyActionLabel}
          </LinkButton>
        ) : null}
      </>
    )
  }

  return (
    <>
      {plans.map((plan) => {
        const hasDifficultyWarning =
          warningDifficulty !== undefined &&
          plan.difficulty !== warningDifficulty
        const isPersonal = plan.ownerType === 'User'
        const viewBase = isPersonal ? '/profile' : teamPath

        return (
          <Button
            key={`${plan.ownerType}-${plan.id}`}
            type='button'
            variant='outline'
            className={cn(
              'w-full justify-between h-fit flex flex-col',
              plan.id === selectedPlanId ? 'border-primary' : '',
            )}
            onClick={() => onSelect(plan)}
          >
            <div className='flex items-center justify-between gap-2 w-full'>
              <div className='flex items-center gap-2 min-w-0'>
                {hasDifficultyWarning ? (
                  <Badge variant='warning' className='gap-1'>
                    <TriangleAlert className='h-3 w-3' />
                    {difficultiesLabels[plan.difficulty]}
                  </Badge>
                ) : null}
                <div className='truncate font-medium'>{plan.name}</div>
                {showOwnerBadge ? (
                  <Badge variant={isPersonal ? 'secondary' : 'outline'}>
                    {isPersonal
                      ? t('dashboard.team.plans.owner-badge.personal')
                      : t('dashboard.team.plans.owner-badge.team')}
                  </Badge>
                ) : null}
              </div>

              <div className='flex items-center gap-2'>
                <div className='text-xs text-muted-foreground whitespace-nowrap'>
                  Created {dayjs(plan.createdAt).fromNow()}
                </div>

                <LinkButton
                  to={`${viewBase}/plans/${plan.id}`}
                  target='_blank'
                  size='xs'
                  className='w-full'
                  onClick={(event) => event.stopPropagation()}
                >
                  view
                </LinkButton>
              </div>
            </div>
            <TeamPlanCompositionSummary
              timeline={plan.timeline}
              className='mt-2 w-full'
            />
          </Button>
        )
      })}
    </>
  )
}

export default RaidPlanCandidatePicker
